import React, { useState, useCallback, memo, useMemo, useEffect } from "react";
import { format, addMonths, subMonths, setMonth, setYear, isSameMonth } from "date-fns";
import { MonthCalendar } from "./MonthCalendar";
import { SessionEvent } from "./SessionEvent";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ProgramColorLegend } from "./ProgramColorLegend";
import { Variation } from "../../types/variation";
import { getCurrentVisibleMonth, saveCurrentVisibleMonth } from "./utils/calendarUtils";

interface SessionCalendarProps {
  events: SessionEvent[];
  isLoading?: boolean; // Added isLoading prop
  variations?: Variation[]; // Make this optional with default in component
  onAddSession?: () => void;
  onEditSession?: (event: SessionEvent) => void;
  onDayClick?: (date: Date) => void;
  currentFilter?: string;
  onFilterChange?: (variationKey: string | undefined) => void;
  selectedProgram?: string; // Added to match the prop being passed
  /** Controlled visible month (e.g. org calendar program bucketing); pair with onCalendarBaseMonthChange. */
  calendarBaseMonth?: Date;
  onCalendarBaseMonthChange?: (date: Date) => void;
}

export const SessionCalendar = memo<SessionCalendarProps>(({
  events,
  isLoading = false, // Default to false
  variations = [], // Default to empty array
  onAddSession,
  onEditSession,
  onDayClick,
  currentFilter,
  onFilterChange,
  selectedProgram, // Added to match the prop being passed
  calendarBaseMonth: calendarBaseMonthProp,
  onCalendarBaseMonthChange,
}) => {
  const [internalBaseMonth, setInternalBaseMonth] = useState(() =>
    getCurrentVisibleMonth()
  );
  const isMonthControlled =
    calendarBaseMonthProp != null && onCalendarBaseMonthChange != null;
  const baseMonth = isMonthControlled ? calendarBaseMonthProp! : internalBaseMonth;
  const [yearView, setYearView] = useState(baseMonth.getFullYear());
  
  // Calculate only two visible months instead of three
  const visibleMonths = useMemo(() => [
    baseMonth,
    addMonths(baseMonth, 1)
  ], [baseMonth]);

  // Save current month whenever it changes
  useEffect(() => {
    saveCurrentVisibleMonth(baseMonth);
    // Update yearView when baseMonth changes
    setYearView(baseMonth.getFullYear());
  }, [baseMonth]);

  const handlePreviousMonth = useCallback(() => {
    if (isMonthControlled) {
      onCalendarBaseMonthChange!(subMonths(calendarBaseMonthProp!, 1));
    } else {
      setInternalBaseMonth((prev) => subMonths(prev, 1));
    }
  }, [
    isMonthControlled,
    calendarBaseMonthProp,
    onCalendarBaseMonthChange,
  ]);

  const handleNextMonth = useCallback(() => {
    if (isMonthControlled) {
      onCalendarBaseMonthChange!(addMonths(calendarBaseMonthProp!, 1));
    } else {
      setInternalBaseMonth((prev) => addMonths(prev, 1));
    }
  }, [
    isMonthControlled,
    calendarBaseMonthProp,
    onCalendarBaseMonthChange,
  ]);

  const handleTodayClick = useCallback(() => {
    const today = new Date();
    if (isMonthControlled) {
      if (!isSameMonth(today, calendarBaseMonthProp!)) {
        onCalendarBaseMonthChange!(today);
      }
    } else {
      setInternalBaseMonth((prev) =>
        isSameMonth(today, prev) ? prev : today
      );
    }
  }, [
    isMonthControlled,
    calendarBaseMonthProp,
    onCalendarBaseMonthChange,
  ]);
  
  // Month names array
  const months = useMemo(() => [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ], []);

  // Year navigation handlers
  const handlePrevYear = useCallback(() => setYearView(prev => prev - 1), []);
  const handleNextYear = useCallback(() => setYearView(prev => prev + 1), []);

  const handleMonthSelect = useCallback(
    (index: number) => {
      const newDate = setMonth(setYear(new Date(), yearView), index);
      if (isMonthControlled) {
        onCalendarBaseMonthChange!(newDate);
      } else {
        setInternalBaseMonth(newDate);
      }
    },
    [yearView, isMonthControlled, onCalendarBaseMonthChange]
  );

  // Loading state rendering
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-muted-foreground">Loading sessions...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        {/* Program Color Legend - placed where the subheading was */}
        <div className="w-3/5">
          <ProgramColorLegend 
            variations={variations} 
            currentFilter={selectedProgram || currentFilter}
            onFilterChange={onFilterChange}
            inline={true}
          />
        </div>
        
        {/* Navigation controls - right aligned */}
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={handleTodayClick}
            className="gap-2"
          >
            Today
          </Button>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={handlePreviousMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon">
                  <Calendar className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="p-4 border-b">
                  <div className="flex justify-between items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrevYear}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="font-semibold">{yearView}</div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextYear}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 p-4">
                  {months.map((month, index) => (
                    <Button
                      key={month}
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMonthSelect(index)}
                      className={cn(
                        "w-full justify-center",
                        format(baseMonth, "MMM") === format(setMonth(new Date(), index), "MMM") &&
                        "bg-accent text-accent-foreground"
                      )}
                    >
                      {format(setMonth(new Date(), index), "MMM")}
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            
            <Button variant="outline" size="icon" onClick={handleNextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {visibleMonths.map((month, index) => (
          <MonthCalendar
            key={format(month, 'MM-yyyy')}
            month={month}
            events={events}
            onEventClick={onEditSession} 
            onDayClick={onDayClick}
            variations={variations}
          />
        ))}
      </div>

      {/* Navigation controls - right aligned */}
      <div className="flex items-center gap-2 justify-end">
        <Button 
          variant="outline" 
          onClick={handleTodayClick}
          className="gap-2"
        >
          Today
        </Button>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={handlePreviousMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon">
                <Calendar className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
              <div className="p-4 border-b">
                <div className="flex justify-between items-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevYear}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="font-semibold">{yearView}</div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextYear}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 p-4">
                {months.map((month, index) => (
                  <Button
                    key={month}
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMonthSelect(index)}
                    className={cn(
                      "w-full justify-center",
                      format(baseMonth, "MMM") === format(setMonth(new Date(), index), "MMM") &&
                      "bg-accent text-accent-foreground"
                    )}
                  >
                    {format(setMonth(new Date(), index), "MMM")}
                  </Button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          
          <Button variant="outline" size="icon" onClick={handleNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>


    </div>
  );
});

SessionCalendar.displayName = "SessionCalendar";
